import * as uuid from "uuid";

import { AlertMessage, ErrorMessage, MUIIcon, ProgressIndicator, Spinner } from "../../widgets";
import { IPage, PageType } from "../../design/page/models";
import { Link, useNavigate } from "react-router-dom";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useCreatePageMutation, useDeletePageMutation, useLazyGetPageMetadataQuery, useLazyGetSitePagesQuery, useUpdatePageMetadataMutation } from "../../services/data";

import { Current_Page_Version } from "../../design/page";
import { useAuth } from "../../../modules/auth";
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, useMediaQuery, useTheme } from "@mui/material";
import { Button } from "react-bootstrap";

export const SitePageList = () => {
    const { siteId } = useAuth()
    const navigate = useNavigate();
    const [searchText, setSearchText] = useState<string>('');
    const ref = useRef<HTMLInputElement>(null)
    const [getPages, { data, isLoading, isError, error }] = useLazyGetSitePagesQuery();
    const [createPage, { isLoading: inserting }] = useCreatePageMutation();
    const [getPageMetadata] = useLazyGetPageMetadataQuery();
    const [updatePageMetadata, { isLoading: updating }] = useUpdatePageMetadataMutation();
    const [deletePage, { isLoading: deleting }] = useDeletePageMutation();
    const [errorMessage, setError] = useState<string>()

    useEffect(() => {
        loadPages()
        // eslint-disable-next-line
    }, [siteId])

    useEffect(() => {
        ref.current?.focus()
    })

    const loadPages = () => {
        if (!siteId)
            return Promise.reject()

        return getPages(siteId).unwrap().then(() => Promise.resolve())
    }

    const handleClonePage = async (page: IPage) => {
        try {
            const pageId = uuid.v1();
            const pageName = `${page.name} - Copy`;

            await createPage({
                ...page,
                id: pageId,
                name: pageName
            }).unwrap();

            const page2 = await getPageMetadata(page.id, true)
                .unwrap();

            await updatePageMetadata({
                ...page2,
                id: pageId,
                name: pageName
            }).unwrap();

            navigate(`/site(${siteId})/design-page(${pageId})`)
        } catch (err) {
            console.error(`Unable to clone the page. <span className='ms-3 fs-8 text-gray-600 font-italic'>${err}<span>`)
        }
    }

    const getFilteredData = useCallback(() => {
        if (!data) return []

        const text = searchText.toLowerCase()

        return data.filter(p => p.name.toLowerCase().indexOf(text) !== -1)
            .sort((a, b) => a.name.localeCompare(b.name))
    }, [data, searchText])

    return (
        <div className="d-flex flex-column gap-5">
            <PageHeader onError={setError} onRefresh={loadPages} />
            <div className="d-flex flex-column gap-2">
                <ErrorMessage message={errorMessage} show={!!errorMessage} />

                <div className="d-flex justify-content-center">
                    <Spinner
                        show={isLoading || inserting || updating || deleting}
                        className="mb-2" message="Please wait ..." />
                </div>

                <AlertMessage
                    show={isError}
                    type="Error"
                    message={(error as any)?.data?.error?.message || `Failed to load site pages`} />

                {data &&
                    <div className="input-group input-group">
                        <input
                            ref={ref}
                            type="search"
                            placeholder="Search"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            className="form-control fs-6" />
                        <span className="input-group-text">
                            <i className="las la-search fs-5"></i>
                        </span>
                    </div>}

                {getFilteredData().map(page =>
                    <div key={page.id} className="card card-body bg-hover-light px-5 py-2 mb-2">
                        <div className="d-flex flex-row align-items-center">
                            <div className="flex-fill me-2">
                                <h6 className="m-0">
                                    <Link to={`/site(${siteId})/page(${page.id})`}>{page.name}</Link>
                                </h6>
                                <div className="fs-7 mt-1">
                                    <label className="text-gray-700 m-0 me-2 fw-semibold">Type: </label>
                                    <span className="fw-bold text-gray-700">{page.type}</span>
                                </div>
                            </div>
                            <div>
                                <Link
                                    to={`/site(${siteId})/design-page(${page.id})`}
                                    className="btn btn-sm py-2 lh-1 btn-primary me-2">
                                    <i className="las la-edit"></i>
                                    Design
                                </Link>

                                <DeleteComponent deletePage={deletePage} page={page} />

                                <button
                                    type="button"
                                    onClick={() => handleClonePage(page)}
                                    className="btn btn-sm py-2 lh-1 btn-secondary">
                                    <i className="las la-clone"></i>
                                    Clone
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>)
}

const DeleteComponent = ({ deletePage, page }: { page: any, deletePage: any }) => {
    const [open, setOpen] = React.useState(false);
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleDeletePage = (page: IPage) => {
        deletePage(page.id).unwrap()
        setOpen(false);
    }
    return (
        <React.Fragment>
            <Button variant="outlined" onClick={handleClickOpen} className="btn btn-sm py-2 lh-1 btn-danger me-2">
                <i className="las la-trash"></i>
                Delete
            </Button>
            <Dialog
                fullScreen={fullScreen}
                open={open}
                onClose={handleClose}
                aria-labelledby="responsive-dialog-title"
            >
                <DialogTitle id="responsive-dialog-title">
                    <h4 className="text-danger">Alert !!</h4>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this page?
                        This action cannot be undone
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button autoFocus onClick={() => handleDeletePage(page)}>
                        Yes
                    </Button>
                    <Button onClick={handleClose} autoFocus>
                        No
                    </Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    )
}

const PageHeader = ({ onError, onRefresh }: {
    onError: (message: string) => void,
    onRefresh: () => Promise<void>
}) => {
    const [createPage] = useCreatePageMutation();
    const [loading, setLoading] = useState<boolean>(false)
    const [refreshing, setRefreshing] = useState<boolean>(false)
    const { siteId: portalId } = useAuth()
    const navigate = useNavigate();

    const createNewPage = () => {
        if (!portalId) {
            return Promise.reject();
        }

        setLoading(true)
        onError('')

        createPage({
            id: uuid.v1(),
            name: '<< Page Name >>',
            title: '<< Page Title >>',
            portalId,
            type: PageType.Card,
            version: Current_Page_Version,
        }).unwrap()
            .then((page) => {
                navigate(`/site(${portalId})/design-page(${page.id})`)
            })
            .catch(err => {
                const message = err.data.message || 'An error occurred while processing your request. Please try again later.'
                onError(message)
            })
            .finally(() => setLoading(false))
    }

    const handleRefresh = () => {
        setRefreshing(true)
        onRefresh()
            .catch(err => {
                const message = err.data.message || 'An error occurred while processing your request. Please try again later.'
                onError(message)
            })
            .finally(() => setRefreshing(false))
    }

    return (
        <div className="d-flex flex-wrap gap-5">
            <div className="flex-fill d-flex align-items-center gap-5">
                <h1 className="my-0 text-gray-700">Site Pages</h1>
            </div>

            <div className="d-flex gap-5 align-items-center">
                <button type="button"
                    disabled={loading}
                    onClick={createNewPage}
                    className="btn btn-primary btn-sm py-2 d-flex gap-1">
                    {(loading) ? <>
                        <ProgressIndicator show={loading} />
                    </> : <>
                        <MUIIcon iconName="AddOutlined" className="me-1" />
                    </>}
                    <span>New</span>
                </button>

                <button type="button"
                    disabled={refreshing}
                    onClick={handleRefresh}
                    title="refresh"
                    className="btn bg-transparent btn-sm py-2 px-0">
                    {(refreshing) ? <>
                        <ProgressIndicator show={refreshing} />
                    </> : <>
                        <MUIIcon iconName="RefreshOutlined" className="me-1" />
                    </>}
                </button>
            </div>
        </div>
    )
}
